import { parseEntityRef as parseCatalogEntityRef } from '@backstage/catalog-model';
import { ReactElement, useMemo, useState } from 'react';
import {
  Chip,
  IconButton,
  makeStyles,
  Typography,
  useTheme,
} from '@material-ui/core';
import {
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import { Launch, AccessTime, InfoOutlined } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Environment } from '@principal-developer-portal/environment-utils';
import {
  appThemeApiRef,
  useAnalytics,
  useApi,
} from '@backstage/core-plugin-api';
import { Theme } from '@material-ui/core/styles';
import {
  CheckResources,
  DueDate,
  GroupInfo,
  IssueData,
  MeasurementResource,
  PdpEntityRow,
  PdpIssue,
  PdpIssueRow,
  PdpResourceIssueRow,
} from './types';
import {
  entityDueDateSortComparator,
  severitySortComparator,
} from './comparators';
import {
  WIZ_FRAMEWORK_IDS,
  InsightsIssueAction,
} from '../../insights/modal/modal-dialog';
import { DueDateInfoModal } from '../../insights/modal/due-date-info-modal';
import type { InsightsIssueActionProps } from '../../insights/modal/modal-dialog';
import { CardItem } from '../../insights/modal/types/support';
import { ExceptionCardInfo } from '../../insights/modal/types/exception';
import { getDueDateString, overdueOrIn7Days } from './date';
import { AutoRemediationBadge } from '../auto-remediation-badge';
import { YardstickIssueResult } from '@internal/yardstick-common';

const useStyles = makeStyles<Theme, { activeTheme: string }>(theme => ({
  chipBackground: {
    marginLeft: '.25em',
    backgroundColor: theme.palette.textSubtle,
    marginBottom: '.25em',
    height: 'auto',
  },
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  chipLabel: props => ({
    color: props.activeTheme === 'principal-dark-theme' ? 'black' : 'white',
    fontWeight: 700,
    fontSize: '12px',
  }),
}));

export const parseEntityRef = (
  entityRef: string,
): { kind: string; name: string; namespace: string } => {
  const [kind, name] = entityRef.split(':');
  const [namespace, entityName] = name.split('/');

  return { kind, name: entityName, namespace };
};

export const renderHeader = (header: string): ReactElement => (
  <Typography
    tabIndex={0}
    style={{
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
    title={header}
  >
    <strong>{header}</strong>
  </Typography>
);

// Component to render due date cell with info modal
const DueDateCell = (props: {
  dueDate: DueDate;
  frameworkIds: string[];
}): ReactElement => {
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const theme = useTheme();
  const { frameworkIds } = props;

  const isFrameworkMatched =
    frameworkIds && WIZ_FRAMEWORK_IDS.some(id => frameworkIds.includes(id));
  const date = getDueDateString(props.dueDate, isFrameworkMatched);

  const { className, dueSoon } = overdueOrIn7Days(props.dueDate);

  const showInfoIcon =
    isFrameworkMatched && props.dueDate.date && date !== 'N/A';

  const handleInfoClick = (): void => {
    setInfoModalOpen(true);
  };

  const handleInfoModalClose = (): void => {
    setInfoModalOpen(false);
  };

  return (
    <>
      <div className="theme--cell-content">
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'nowrap',
              gap: '4px',
            }}
          >
            {dueSoon ? (
              <Typography
                style={{
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                <AccessTime
                  className={className}
                  style={{ fontSize: '20px', marginRight: '4px' }}
                />{' '}
                <strong>{date}</strong>
              </Typography>
            ) : (
              <Typography style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                {date}
              </Typography>
            )}
            {showInfoIcon && (
              <IconButton
                onClick={handleInfoClick}
                aria-label="View information about due dates for staging framework findings"
                size="small"
                style={{
                  padding: 0,
                  color: theme.palette.link,
                  flexShrink: 0,
                  // Shifts icon upward to align with top of due date
                  marginTop: '-10px',
                }}
              >
                <InfoOutlined style={{ fontSize: '16px' }} />
              </IconButton>
            )}
          </div>
          {props.dueDate.text !== 'N/A' && (
            <Typography style={{ fontSize: '12px' }}>
              {props.dueDate.text}
            </Typography>
          )}
        </div>
      </div>
      <DueDateInfoModal open={infoModalOpen} onClose={handleInfoModalClose} />
    </>
  );
};

// Wrapper function for MUI DataGrid renderCell
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, func-style
export function renderDueDate(
  params: GridRenderCellParams<{ dueDate: DueDate; frameworkIds: string[] }>,
) {
  return (
    <DueDateCell
      dueDate={params.row.dueDate}
      frameworkIds={params.row.frameworkIds}
    />
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, func-style
export function renderSeverity(
  params: GridRenderCellParams<{ severity: string }>,
) {
  const severity =
    params.row.severity[0].toUpperCase() +
    params.row.severity.slice(1).toLowerCase();

  /* eslint-disable no-nested-ternary */
  return (
    <div className="theme--cell-content">
      {severity && severity !== 'Low' ? (
        severity === 'Critical' || severity === 'Immediate' ? (
          <Typography style={{ fontSize: '14px' }}>
            <strong>{severity}</strong>
          </Typography>
        ) : (
          <Typography style={{ fontSize: '14px' }}>{severity}</Typography>
        )
      ) : (
        <Typography style={{ fontSize: '14px' }}>-</Typography>
      )}
    </div>
  );
  /* eslint-enable no-nested-ternary */
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getFindingCatAndTitle = (params: GridRenderCellParams) => {
  const category = params.row.measurement;
  let title = params.row.issueType;

  if (typeof title === 'string' && title.includes('**')) {
    title = 'Details restricted to owning team';
  }

  return { category, title };
};

// eslint-disable-next-line no-warning-comments
// TODO: use this version of render after additional user testing
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, func-style, max-lines-per-function
// export function IndividualIssue(params: GridRenderCellParams) {
//   const themeConfig = useApi(appThemeApiRef);
//   const activeTheme = themeConfig.getActiveThemeId();
//   const classes = useStyles({activeTheme: activeTheme ?? 'default-theme'});
//   const {category, title} = getFindingCatAndTitle(params);

//   return (
//     <div className="theme--cell-content">
//       <div>
//         <Typography
//           style={{
//             fontSize: '12px',
//             whiteSpace: 'normal',
//             wordBreak: 'break-word',
//             overflowWrap: 'break-word',
//             textOverflow: 'ellipsis',
//             overflow: 'hidden',
//             display: '-webkit-box',
//             WebkitBoxOrient: 'vertical',
//             WebkitLineClamp: 2,
//           }}
//           title={category}
//         >
//           {category}
//         </Typography>
//       </div>
//       <Typography
//         style={{
//           fontSize: '14px',
//           wordBreak: 'break-word',
//           overflowWrap: 'break-word',
//           textOverflow: 'ellipsis',
//           overflow: 'hidden',
//           display: '-webkit-box',
//           WebkitBoxOrient: 'vertical',
//           WebkitLineClamp: 2,
//         }}
//       >
//         <strong>{title}</strong>
//         {params.row.grouped && params.row.issues.length > 1 && (
//           <Chip
//             size="small"
//             label={<Typography className={classes.chipLabel}>{params.row.issues.length} Findings</Typography>}
//             className={classes.chipBackground}
//           />
//         )}
//       </Typography>
//       {(params.row.issueType?.toLowerCase().includes('cspm') || params.row.issueType?.toLowerCase().includes('nvm')) && (
//         <Typography
//           style={{
//             fontSize: '12px',
//             whiteSpace: 'normal',
//             wordBreak: 'break-word',
//             overflowWrap: 'break-word',
//           }}
//         >
//           <strong>Resource ID:</strong> {params.row.resourceID}
//         </Typography>
//       )}
//     </div>
//   );
// }

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, func-style, max-lines-per-function
export function IndividualIssue(params: GridRenderCellParams) {
  const themeConfig = useApi(appThemeApiRef);
  const activeTheme = themeConfig.getActiveThemeId();
  const classes = useStyles({ activeTheme: activeTheme ?? 'default-theme' });

  return (
    <div className="theme--cell-content">
      <Typography
        component="div"
        style={{
          fontSize: '12px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.25rem',
          alignItems: 'center',
        }}
      >
        <span>{params.row.measurement || params.row.issueType || 'Issue'}</span>
        {params.row.grouped && params.row.issues.length > 1 && (
          <Chip
            size="small"
            label={
              <Typography className={classes.chipLabel}>
                {params.row.issues.length} Findings
              </Typography>
            }
            className={classes.chipBackground}
          />
        )}
        {params.row.autoFix && <AutoRemediationBadge />}
      </Typography>
      <div>
        <Typography
          style={{
            fontSize: '16px',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
          }}
          title={params.row.issueType}
        >
          {params.row.issueType}
        </Typography>
      </div>

      {params.row.checkId?.toLowerCase().includes('cspm') &&
        !params.row.grouped && (
          <Typography
            style={{
              fontSize: '12px',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            <strong>Resource Name:</strong> {params.row.resourceName}
          </Typography>
        )}
      {params.row.checkId?.toLowerCase().includes('nvm') &&
        !params.row.grouped && (
          <Typography
            style={{
              fontSize: '12px',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            <strong>Resource ID:</strong> {params.row.resourceID}
          </Typography>
        )}
    </div>
  );
}

// Extract entity display name logic
export const getEntityDisplayName = (entityName: string): string => {
  return entityName.replace(/-mc$/, '');
};

// Extract secondary info logic
export const getSecondaryInfo = (
  namespace: string,
  kind: string,
  accountId?: string,
  cloudPlatform?: string,
): string => {
  if (kind.toLowerCase() === 'resource') {
    const resourceNamespace = namespace === 'default' ? 'AWS' : namespace;

    return `${resourceNamespace}`;
  }

  if (namespace === 'default') {
    // If namespace is "default", try to show more meaningful information
    if (cloudPlatform && accountId) {
      return `${cloudPlatform}: ${accountId}`;
    }

    if (accountId) {
      return `Account: ${accountId}`;
    }
  }

  return namespace;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, func-style
export function renderEntity(params: GridRenderCellParams) {
  const { kind } = parseEntityRef(params.row.entityInfo.entityRef);
  const link = `/catalog/${params.row.entityInfo.entityNamespace}/${kind}/${params.row.entityInfo.entityName}`;

  // Use the extracted utility functions
  const description = getEntityDisplayName(params.row.entityInfo.entityName);
  const secondaryInfo = getSecondaryInfo(
    params.row.entityInfo.entityNamespace,
    kind,
    params.row.accountId,
    params.row.cloudPlatform,
  );

  return (
    <div className="theme--cell-content">
      <div>
        <Link to={link} target="_blank">
          <Typography
            className="theme--text-link"
            style={{
              fontSize: '14px',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {description}
          </Typography>
        </Link>
        <Typography
          style={{
            fontSize: '12px',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {secondaryInfo}
        </Typography>
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, func-style
export function renderOwner(params: GridRenderCellParams) {
  const rawOwner = params.row.owningTeam as string | undefined;
  if (!rawOwner) return null;

  let owner: string;
  let org: string;
  try {
    const parsed = parseCatalogEntityRef(rawOwner, {
      defaultKind: 'group',
      defaultNamespace: 'default',
    });
    owner = parsed.name;
    org = parsed.namespace;
  } catch {
    return null;
  }

  return (
    <Link to={`/catalog/${org}/group/${owner}`} target="_blank">
      <Typography
        className="theme--text-link"
        style={{
          fontSize: '14px',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {owner}
      </Typography>
    </Link>
  );
}

// eslint-disable-next-line max-lines-per-function
export const retrieveSupportInfo = (issueType: string): CardItem[] => {
  const supportInfo: CardItem[] = [];

  switch (issueType) {
    case 'CSPM': {
      supportInfo.push({
        type: 'documentation',
        title: 'CSPM Support',
        description: 'Documentation for CSPM support',
        href: 'https://portal.developer.principal.com/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/pdp/support-links/#support-links-for-cspm-wiz',
      });
      break;
    }
    case 'PendingCSPM': {
      supportInfo.push(
        {
          type: 'documentation',
          title: 'Pending CSPM Findings',
          description: 'CSPM Activation Roadmap documentation',
          href: 'https://principal.atlassian.net/wiki/spaces/ISSECURE/pages/612831491/Activation+Roadmap',
        },
        {
          type: 'documentation',
          title: 'CSPM Support',
          description: 'Documentation for CSPM Support',
          href: 'https://principal.service-now.com/esc?id=sc_cat_item&table=sc_cat_item&sys_id=8b2afe701b2139143dbb0d45ec4bcb0f&recordUrl=com.glideapp.servicecatalog_cat_item_view.do%3Fv%3D1&sysparm_id=8b2afe701b2139143dbb0d45ec4bcb0f',
        },
      );
      break;
    }
    case 'NVM': {
      supportInfo.push({
        type: 'documentation',
        title: 'NVM Support',
        description: 'Documentation for NVM support',
        href: 'https://portal.developer.principal.com/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/pdp/support-links/#support-links-for-cloud-network-vulnerability-management-nvm',
      });
      break;
    }
    case 'APPID': {
      supportInfo.push({
        type: 'documentation',
        title: 'Component health',
        description: 'Documentation for component health requirements',
        href: 'https://portal.developer.principal.com/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/pdp/support-links/#support-links-for-component-health',
      });
      break;
    }
    case 'APIs': {
      supportInfo.push(
        {
          type: 'support',
          title: 'API Platform Engineering',
          description: 'Contact us if you have any questions',
          href: 'https://principal.service-now.com/esc?id=sc_cat_item_guide&table=sc_cat_item&sys_id=469bbe2a476d6d90291c87de536d43b2',
        },
        {
          type: 'documentation',
          title: 'Swagger Editor',
          description: 'Design, document and test REST APIs visually',
          href: 'https://portal.developer.principal.com/toolbox#swagger-editor',
        },
        {
          type: 'documentation',
          title: 'Spectral Editor',
          description:
            'Ensure REST API quality with real-time linting and validation',
          href: 'https://portal.developer.principal.com/toolbox#spectral-editor',
        },
        {
          type: 'other',
          title: 'Community Support',
          description: 'Microsoft Teams channel for API and Messaging support',
          href: 'https://teams.microsoft.com/l/channel/19%3A664a32ab2e464c7dbd9400f341e4aed4%40thread.tacv2/API%20and%20Messaging?groupId=e534b89f-cad9-4262-8c7b-e1147d3bf852&tenantId=3bea478c-1684-4a8c-8e85-045ec54ba430',
        },
      );
      break;
    }
    default: {
      supportInfo.push({
        type: 'documentation',
        title: 'PDP support',
        description: 'Documentation for PDP support',
        href: 'https://portal.developer.principal.com/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/pdp/support-links/#support-links-for-pdp',
      });
      break;
    }
  }

  return supportInfo;
};

export const retrieveIssueInfo = (
  issueTitle?: string,
  frameworkIds?: string[],
): { issueType: string; issueCategory: 'resource' | 'general' } => {
  /* eslint-disable no-nested-ternary */
  const issueType =
    frameworkIds && WIZ_FRAMEWORK_IDS.some(id => frameworkIds.includes(id))
      ? 'PendingCSPM'
      : issueTitle?.includes('CSPM')
      ? 'CSPM'
      : issueTitle?.includes('NVM')
      ? 'NVM'
      : issueTitle?.includes('APPID')
      ? 'APPID'
      : issueTitle?.includes('APIs')
      ? 'APIs'
      : 'general';
  /* eslint-enable no-nested-ternary */
  const issueCategory =
    issueType === 'CSPM' || issueType === 'PendingCSPM' || issueType === 'NVM'
      ? 'resource'
      : 'general';

  return { issueType, issueCategory };
};

// Helper function to extract exception card items
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractExceptionCardItems = (row: any): ExceptionCardInfo[] => {
  const exceptionCardItems: ExceptionCardInfo[] = [];

  if (row.grouped) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    row.issues.forEach((issue: any) => {
      const info = issue?._meta?.exceptionInfo;

      if (info?.href && info?.name && info?.description) {
        exceptionCardItems.push({
          name: info.name,
          href: info.href,
          description: info.description,
        });
      }
    });
  } else {
    const exceptionInfo = row._meta?.exceptionInfo;

    if (
      exceptionInfo?.href &&
      exceptionInfo?.name &&
      exceptionInfo?.description
    ) {
      exceptionCardItems.push({
        name: exceptionInfo.name,
        href: exceptionInfo.href,
        description: exceptionInfo.description,
      });
    }
  }

  return [
    ...new Map(exceptionCardItems.map(item => [item.name, item])).values(),
  ];
};

// Resolves the handbook removal-timing link for a finding from the owning check's resources.
// Returns undefined when the check does not publish one.
export const getRemovalTimingHref = (
  resources?: MeasurementResource[],
): string | undefined => {
  return resources?.find(resource =>
    resource?.href?.includes('data-refresh-frequency/#'),
  )?.href;
};

/**
 * Builds InsightsIssueActionProps from a PdpResourceIssueRow for use with InsightsIssueAction modal
 * @param row - The issue row to build props from (can be partial)
 * @returns InsightsIssueActionProps object for the modal
 */
export const buildInsightsIssueActionProps = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: Partial<PdpResourceIssueRow> & { subtitle?: string; [key: string]: any },
): Omit<InsightsIssueActionProps, 'open' | 'onClose'> => {
  const { kind } = row.entityInfo?.entityRef
    ? parseEntityRef(row.entityInfo.entityRef)
    : { kind: 'default' };
  const emailRef = row.owningTeam?.split('/')[1] ?? '';

  const { issueType } = retrieveIssueInfo(row.issueType, row.frameworkId);

  const url = `/catalog/${
    row.entityInfo?.entityNamespace ?? 'default'
  }/${kind}/${row.entityInfo?.entityName ?? 'unknown'}/insights`;
  const teamUrl = `/catalog/default/group/${emailRef}`;

  let resourceType;
  if (kind?.toLowerCase() === 'resource') {
    resourceType =
      row.entityInfo?.entityNamespace === 'default'
        ? 'AWS'
        : row.entityInfo?.entityNamespace;
  } else {
    resourceType = row.cloudPlatform;
  }

  // Get support information
  const supportInfo: CardItem[] = row.resources?.length
    ? row.resources
    : retrieveSupportInfo(issueType);

  // Get exception card items
  const exceptionCardItems = extractExceptionCardItems(row);

  const removalTimingHref = getRemovalTimingHref(row.resources);

  return {
    appbar: {
      title: row.issueType ?? 'Issue Details',
      subtitle: row.subtitle ?? '',
      timeStamp:
        row.updatedAt instanceof Date
          ? row.updatedAt.toISOString()
          : row.updatedAt?.toString() ?? '',
      foundDate:
        row.foundDate instanceof Date
          ? row.foundDate.toISOString()
          : row.foundDate?.toString(),
      removalTimingHref,
    },
    supportCard: {
      cardItems: supportInfo,
    },
    exceptionCard: {
      exceptionCardItems,
    },
    modalContent: {
      contentProps: {
        gridRows: [
          {
            due_date: {
              text: row.dueDate?.text ?? 'Not available',
              date: row.dueDate?.date ?? null,
            },
            entity: {
              text: row.entityInfo?.entityName ?? 'No entity',
              link: url,
              type: kind?.toLowerCase(),
              nameSpace: row.entityInfo?.entityNamespace ?? '',
              resourceType: resourceType ?? '',
              accountId: row.accountId ?? '',
            },
            owning_team: {
              text: row.owningTeam ?? 'No team info found',
              link: teamUrl,
            },
            severity: {
              text:
                row.severity && row.severity?.toLowerCase() !== 'low'
                  ? row.severity
                  : '-',
            },
          },
        ],
        type: kind?.toLowerCase() === 'resource' ? 'resource' : 'general',
        issueName: row.issueName ?? 'No issue name available',
        resourceID: row.resourceID ?? 'No ARN available',
        issueDescription: row.issueDetails ?? row.issueDescription ?? '',
        resourceName: row.resourceName,
        remediationSteps:
          row.remediationSteps ?? 'No remediation steps available',
        group: row.grouped,
        issues: row.issues,
        frameworkIds: row.frameworkIds,
        issueId: row.issueId,
        autoFix: row.autoFix,
        entityRef: row.entityInfo?.entityRef,
        checkId: row.checkId,
      },
    },
  };
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, func-style, max-lines-per-function, complexity
export function RenderAction(params: GridRenderCellParams) {
  const [open, setOpen] = useState(false);
  const analytics = useAnalytics();
  const supportInfo: CardItem[] = [];
  const kind = params.row.entityInfo?.entityRef?.split(':')[0] ?? 'default';
  const emailRef = params.row.owningTeam?.split('/')[1] ?? '';
  // eslint-disable-next-line no-warning-comments
  // TODO bring back after additional user testing
  // const {category, title} = getFindingCatAndTitle(params);

  const { issueType } = retrieveIssueInfo(
    params.row.issueType,
    params.row.frameworkIds,
  );
  const analyticsIssueType = params.row.name ?? params.row.checkId;

  const url = `/catalog/${params.row.entityInfo.entityNamespace}/${kind}/${params.row.entityInfo.entityName}/insights`;
  const teamUrl = `/catalog/default/group/${emailRef}`;

  const uniqueExceptionCardItems = extractExceptionCardItems(params.row);

  let resourceType;
  if (kind?.toLowerCase() === 'resource') {
    resourceType =
      params.row.entityInfo?.entityNamespace === 'default'
        ? 'AWS'
        : params.row.entityInfo?.entityNamespace;
  } else {
    resourceType = params.row.cloudPlatform;
  }

  const handleClickOpen = (): void => {
    setOpen(true);
    analytics.captureEvent('insights:open_issue_modal', 'insights-actions', {
      attributes: {
        entity: params.row.entityInfo.entityRef,
        issueType: analyticsIssueType,
        severity: params.row.severity ?? '-',
        dueDate: params.row.dueDate ?? '-',
      },
    });
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  // NOTE: Remove after all checks have resources
  if (issueType) {
    supportInfo.push(...retrieveSupportInfo(issueType));
  }

  return (
    <Typography
      className="theme--cell-content"
      style={{ textAlign: 'center', minWidth: '56px', maxWidth: '65px' }}
    >
      <IconButton
        className="theme--text-link"
        aria-label={`See details for ${params.row.issueType} on ${params.row.entityInfo.entityRef}`}
        onClick={handleClickOpen}
      >
        <Launch />
      </IconButton>
      <InsightsIssueAction
        open={open}
        onClose={handleClose}
        // eslint-disable-next-line no-warning-comments
        // TODO: switch back to this after additional user testing
        // appbar={{
        //   title,
        //   subtitle: category,
        //   timeStamp: params.row.updatedAt ?? '',
        // }}
        appbar={{
          title: params.row.measurement,
          subtitle: params.row.issueType,
          timeStamp: params.row.updatedAt ?? '',
          foundDate: params.row.foundDate?.toString(),
        }}
        supportCard={{
          cardItems: params.row.resources?.length
            ? params.row.resources
            : supportInfo,
        }}
        exceptionCard={{
          exceptionCardItems: uniqueExceptionCardItems,
        }}
        modalContent={{
          contentProps: {
            gridRows: [
              {
                due_date: {
                  text: params.row.dueDate?.text ?? 'Not available',
                  date: params.row.dueDate?.date ?? null,
                },
                entity: {
                  text: params.row.entityInfo?.entityName ?? 'No entity',
                  link: url ?? '',
                  type: kind?.toLowerCase(),
                  nameSpace: params.row.entityInfo?.entityNamespace ?? '',
                  resourceType: resourceType ?? '',
                  accountId: params.row.accountId ?? '',
                },
                owning_team: {
                  text: params.row.owningTeam ?? 'No team info found',
                  link: teamUrl ?? '',
                },
                severity: {
                  text:
                    params.row.severity &&
                    params.row.severity?.toLowerCase() !== 'low'
                      ? params.row.severity
                      : '-',
                },
              },
            ],
            type: kind?.toLowerCase() === 'resource' ? 'resource' : 'general',
            issueName: params.row.issueName ?? 'No issue name available',
            resourceID: params.row.resourceID ?? 'No ARN available',
            issueDescription: params.row.issueDescription,
            resourceName: params.row.resourceName,
            region: params.row.region ?? 'No information for region available',
            remediationSteps:
              params.row.remediationSteps ?? 'No remediation steps available',
            group: params.row.grouped,
            issues: params.row.issues,
            frameworkIds: params.row.frameworkIds,
            issueId: params.row.issueId,
            autoFix: params.row.autoFix,
            entityRef: params.row.entityInfo?.entityRef,
            checkId: params.row.checkId,
            dueDateText: params.row.dueDate?.text ?? 'Not available',
          },
        }}
      />
    </Typography>
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, func-style
export function EntityAction(params: GridRenderCellParams) {
  const ref = params.row.entityInfo.entityRef;
  const resource = ref.split(':')[0];
  const analytics = useAnalytics();

  const url = `/catalog/${params.row.entityInfo.entityNamespace}/${resource}/${params.row.entityInfo.entityName}/insights`;

  return (
    <Typography
      className="theme--cell-content"
      style={{ fontSize: '12px' }}
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      onClick={() => {
        analytics.captureEvent(
          'insights:open_entity_modal',
          'insights-actions',
          {
            attributes: {
              entity: params.row.entityInfo.entityRef,
              issuesCount: params.row.issues,
              severity: params.row.severity ?? '-',
              dueDate: params.row.dueDate ?? '-',
            },
          },
        );
      }}
    >
      <Link className="theme--text-link" to={url} target="_blank">
        <strong>{params.row.issues} needed actions</strong>{' '}
        <Launch style={{ fontSize: '14px', marginBottom: '-2.5px' }} />
      </Link>
    </Typography>
  );
}
const groupInfo: GroupInfo[] = [
  {
    group: 'catalog-info-validation',
    title: 'Catalog info contains missing or invalid values',
    description: 'Update catalog-info.yaml file',
    measurement: 'Valid catalog-info.yaml file',
    check: ['catalog-info-validation'],
  },
  {
    group: 'enterprise-pipeline-version-check',
    title: 'Enterprise Pipeline Version Check',
    description: 'Update enterprise pipeline and module versions',
    measurement: 'Enterprise Pipeline Compliance',
    check: [
      'enterprise-pipeline-health-check',
      'quality-check-module-health-check',
      'package-module-health-check',
      'deploy-module-health-check',
      'acceptance-module-health-check',
      'maven-jar-library-health-check',
      'nuget-library-health-check',
    ],
  },
];

export const determineIssueResources = (
  checkId: string,
  checks: CheckResources[],
): MeasurementResource[] => {
  const resources = checks?.find(
    checkResource => checkResource.checkId === checkId,
  );

  return resources?.resources || [];
};

// Helper function to get measurement name from check registry
export const getMeasurementName = (
  finding: PdpIssue | YardstickIssueResult,
  checks: CheckResources[],
): string | undefined => {
  const checkId = finding?.from;
  const foundCheck = checks?.find(c => c.checkId === checkId);
  const measurementName =
    foundCheck?.measurementName ||
    (finding.measurement && finding.measurement.trim() !== ''
      ? finding.measurement
      : foundCheck?.title);

  return measurementName;
};

// eslint-disable-next-line max-lines-per-function
export const determineIssueRows = (
  data: IssueData,
  checks: CheckResources[],
): PdpIssueRow[] | PdpResourceIssueRow[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: any[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const groupedRows: Record<string, any> = {};

  if (data) {
    const entities = Object.values(data?.entities);

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    Array.isArray(entities) &&
      // eslint-disable-next-line max-lines-per-function
      entities.forEach(entity => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-lines-per-function, complexity
        if (entity.issues && Array.isArray(entity.issues)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-lines-per-function, complexity
          entity.issues.forEach((issue: any) => {
            const issueType =
              // eslint-disable-next-line no-nested-ternary
              issue.from?.includes('cspm') || issue.from?.includes('nvm')
                ? 'wiz'
                : groupInfo.some(group => group.check.includes(issue.from))
                ? 'group'
                : 'general';

            const { name, namespace } = parseEntityRef(entity.entity.ref);

            switch (issueType) {
              case 'general': {
                rows.push({
                  id: `${entity.entity.ref}/${issue.id}`,
                  dueDate: issue.dueDate,
                  severity: issue.severity,
                  name: issue.name,
                  owningTeam: entity.entity.owner,
                  entityInfo: {
                    entityName: name,
                    entityNamespace: namespace,
                    entityRef: entity.entity.ref,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'github.com/project-slug':
                      entity.entity['github.com/project-slug'],
                  },
                  issueType: issue.title,
                  measurement: getMeasurementName(issue, checks) || issue.title,
                  issueDescription: issue.message,
                  accountId: entity.entity.resourceId,
                  issueDetails: issue?._meta?.issueDetails || issue.message,
                  remediationSteps:
                    issue?._meta?.remediationSteps?.value ||
                    issue?._meta?.remediationSteps,
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  _meta: issue?._meta,
                  updatedAt: issue.lastUpdated,
                  foundDate: issue.foundDate,
                  resources: determineIssueResources(issue.from, checks),
                  checkId: issue.from,
                  domainId: issue?._meta?.domainId,
                  issueId: issue.id || issue._meta.issueId || '',
                  autoFix: issue.autoFix, // Pass through autoFix config
                });
                break;
              }
              case 'wiz': {
                rows.push({
                  id: `${entity.entity.ref}/${issue.id}`,
                  dueDate: issue.dueDate,
                  severity: issue.severity,
                  owningTeam: entity.entity.owner,
                  name: issue.name,
                  entityInfo: {
                    entityName: name,
                    entityNamespace: namespace,
                    entityRef: entity.entity.ref,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'github.com/project-slug':
                      entity.entity['github.com/project-slug'],
                  },
                  issueType: issue.title,
                  measurement: getMeasurementName(issue, checks) || issue.title,
                  issueDescription: issue.message,
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  _meta: issue._meta,
                  accountId: entity.entity.resourceId,
                  cloudPlatform: entity.entity.cloudPlatform,
                  frameworkIds: issue._meta.frameworkIds,
                  issueId: issue.id || issue._meta.issueId || '', // Use root level id first, fallback to _meta.issueId
                  issueName: issue._meta.issueName ?? '',
                  issueDetails: issue.message || issue?._meta?.issueDetails,
                  resourceID:
                    issue?._meta?.resourceArn || issue?._meta?.resourceId,
                  resourceName: issue?._meta?.resourceName ?? '',
                  region: issue?._meta?.region || '',
                  remediationSteps: issue._meta.remediationSteps,
                  updatedAt: issue.lastUpdated,
                  foundDate: issue.foundDate,
                  resources: determineIssueResources(issue.from, checks),
                  checkId: issue.from,
                  domainId: issue?._meta?.domainId,
                  autoFix: issue.autoFix, // Pass through autoFix config
                });
                break;
              }
              case 'group': {
                let group = groupInfo.find(g => g.check.includes(issue.from));

                if (
                  group?.group === 'catalog-info-validation' &&
                  Array.isArray(entity.issues) &&
                  entity.issues.length === 1 &&
                  issue.id.includes('lean-ix-appid') &&
                  issue.focus === 'ent-required'
                ) {
                  group = {
                    group: 'catalog-info-validation',
                    title: 'Invalid Linked LeanIX AppID',
                    measurement: 'Valid catalog-info.yaml file',
                    description:
                      'Update catalog-info.yaml file to include the LeanIX AppID',
                    check: ['catalog-info-validation'],
                  };
                }

                if (groupedRows[`${entity.entity.ref}/${group?.group}`]) {
                  groupedRows[
                    `${entity.entity.ref}/${group?.group}`
                  ].issues.push({
                    id: issue.id, // Add id for issue lookup
                    issueType: issue.title,
                    dueDate: issue.dueDate,
                    severity: issue.severity,
                    focus: issue.focus,
                    issueDescription: issue.message,
                    name: issue.name,
                    issueDetails: issue?._meta?.issueDetails || issue.message,
                    remediationSteps:
                      issue?._meta?.remediationSteps?.value ||
                      issue?._meta?.remediationSteps,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    _meta: issue?._meta,
                    checkId: issue.from,
                    domainId: issue?._meta?.domainId,
                    autoFix: issue.autoFix, // Pass through autoFix config
                  });
                  groupedRows[
                    `${entity.entity.ref}/${group?.group}`
                  ].issueDetails += issue?.details
                    ? `\n\n**${issue.title}**\n\n${issue.details}`
                    : `\n\n**${issue.title}**\n\n${issue.message}`;
                  groupedRows[
                    `${entity.entity.ref}/${group?.group}`
                  ].remediationSteps += `\n\n**${issue.title}**\n\n${issue?._meta?.remediationSteps}`;
                  groupedRows[`${entity.entity.ref}/${group?.group}`].autoFix =
                    groupedRows[`${entity.entity.ref}/${group?.group}`].autoFix;
                } else {
                  groupedRows[`${entity.entity.ref}/${group?.group}`] = {
                    entityInfo: {
                      entityName: name,
                      entityNamespace: namespace,
                      entityRef: entity.entity.ref,
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      'github.com/project-slug':
                        entity.entity['github.com/project-slug'],
                    },
                    accountId: entity.entity.resourceId,
                    cloudPlatform: entity.entity.cloudPlatform,
                    issueId: `${entity.entity.ref}/${issue.from}`,
                    issueType: group?.title,
                    measurement: group?.measurement,
                    issueDescription: group?.description,
                    issueDetails: issue?.details
                      ? `\n\n**${issue.title}**\n\n${issue.details}`
                      : `\n\n**${issue.title}**\n\n${issue.message}`,
                    remediationSteps: `**${issue.title}**\n\n${issue?._meta?.remediationSteps}`,
                    updatedAt: issue.lastUpdated,
                    owningTeam: entity.entity.owner,
                    resources: determineIssueResources(issue.from, checks),
                    checkId: issue.from,
                    issues: [
                      {
                        id: issue.id, // Add id for issue lookup
                        issueType: issue.title,
                        dueDate: issue.dueDate,
                        severity: issue.severity,
                        focus: issue.focus,
                        issueDescription: issue.message,
                        issueDetails:
                          issue?._meta?.issueDetails || issue.message,
                        remediationSteps:
                          issue?._meta?.remediationSteps?.value ||
                          issue?._meta?.remediationSteps,
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        _meta: issue?._meta,
                        checkId: issue.from,
                        name: issue.name,
                        domainId: issue?._meta?.domainId,
                        autoFix: issue.autoFix, // Pass through autoFix config
                      },
                    ],
                  };
                }

                break;
              }
              default:
              /* empty */
            }
          });
        }
      });
  }

  if (groupedRows) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const groups: any = Object.values(groupedRows);

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    Array.isArray(groups) &&
      groups.forEach(group => {
        if (group.issues.length > 0) {
          const names = group.issues
            ?.map((issue: { name: string }) => issue.name)
            .filter((name: string) => name?.trim());
          const uniqueNames = [...new Set(names)];
          const name: string =
            uniqueNames.length > 0
              ? `${group.checkId}: ${uniqueNames.join(', ')}`
              : group.checkId;

          rows.push({
            id: group.issueId,
            issueType: group.issueType,
            measurement: group.measurement,
            issueDescription: group.issueDescription,
            issueDetails: group.issueDetails,
            remediationSteps: group.remediationSteps,
            updatedAt: group.updatedAt,
            dueDate: group.issues.sort(
              (
                a: { dueDate: { date: Date } },
                b: { dueDate: { date: Date } },
              ) =>
                entityDueDateSortComparator(a.dueDate?.date, b.dueDate?.date),
            )[0]?.dueDate || { date: null, text: 'N/A' },
            severity:
              group.issues.sort(
                (a: { severity: string }, b: { severity: string }) =>
                  severitySortComparator(a.severity, b.severity),
              )[0]?.severity || 'low',
            owningTeam: group.owningTeam,
            entityInfo: group.entityInfo,
            issues: group.issues,
            accountId: group.accountId,
            cloudPlatform: group.cloudPlatform,
            resources: group.resources,
            grouped: true,
            checkId: group.checkId,
            name,
            autoFix: group.autoFix,
          });
        }
      });
  }

  return rows;
};

export const determineEntityRows = (data: IssueData): PdpEntityRow[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: any = [];
  const defaultDueDate: DueDate = { date: null, text: 'N/A' };

  if (data) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entities: any = Object.values(data?.entities);

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    Array.isArray(entities) &&
      entities.forEach(entity => {
        if (entity.issues.length > 0) {
          const { name, namespace } = parseEntityRef(entity.entity.ref);

          rows.push({
            id: entity.entity.ref,
            dueDate:
              entity.issues.sort(
                (
                  a: { dueDate: { date: Date } },
                  b: { dueDate: { date: Date } },
                ) =>
                  entityDueDateSortComparator(a.dueDate?.date, b.dueDate?.date),
              )[0]?.dueDate || defaultDueDate,
            severity:
              entity.issues.sort(
                (a: { severity: string }, b: { severity: string }) =>
                  severitySortComparator(a.severity, b.severity),
              )[0]?.severity || 'low',
            owningTeam: entity.entity.owner,
            entityInfo: {
              entityName: name,
              entityNamespace: namespace,
              entityRef: entity.entity.ref,
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'github.com/project-slug':
                entity.entity['github.com/project-slug'],
            },
            issues: entity.issues.length,
            accountId: entity.entity.resourceId,
            cloudPlatform: entity.entity.cloudPlatform,
            issueData: determineIssueRows(data, []),
          });
        }
      });
  }

  return rows;
};

export const getPDPCatalogUrl = function getPDPCatalogUrl(
  env: Environment,
  kind: string,
  entityName: string,
  entityNamespace: string,
): string {
  let url: string;

  switch (env) {
    case Environment.Local:
    case Environment.Dev: {
      url = `https://portal.developer.dev.principal.com`;
      break;
    }
    case Environment.Staging: {
      url = `https://portal.developer.staging.principal.com`;
      break;
    }
    case Environment.Prod: {
      url = `https://portal.developer.principal.com`;
      break;
    }
    default: {
      throw new Error('Not supported');
    }
  }

  return `${url}/catalog/${entityNamespace}/${kind}/${entityName}`;
};

/**
 * Accept environment as a parameter so the entity link matches the current UI environment
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const determineExcelData = (
  data: IssueData | PdpIssueRow[] | PdpResourceIssueRow[],
  environment: Environment,
): any[] => {
  // Type guard to check if data is IssueData
  function isIssueData(obj: any): obj is IssueData {
    return obj && typeof obj === 'object' && 'entities' in obj;
  }

  const resolvedIssueData = isIssueData(data)
    ? determineIssueRows(data, [])
    : data;
  const rows = resolvedIssueData.map(row => {
    const { kind } = parseEntityRef(row.entityInfo.entityRef);
    const resourceId =
      row._meta?.resourceId || row._meta?.resourceArn || row.accountId;

    return {
      Severity: row.severity?.toLowerCase() === 'low' ? '' : row.severity,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Measurement Title': row.measurement,
      // Only populated for NVM/CSPM findings, blank for other measurements
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Source Issue ID': row._meta?.issueId ?? '',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Finding Title': row.issueType,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Finding Description': row.issueDescription,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Finding Details': row.issueDetails || row._meta?.issueName?.value,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Remediation Steps': row.remediationSteps,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Due Date': row.dueDate.date,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Due Date Text': row.dueDate.text,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Last Scanned': row.updatedAt,
      Owner: row.owningTeam,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Catalog Entity':
        row._meta?.cloudPlatform === 'AWS'
          ? `${row.entityInfo.entityName}`
          : `${row.entityInfo.entityNamespace}/${row.entityInfo.entityName}`,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Entity Link': getPDPCatalogUrl(
        environment,
        kind,
        row.entityInfo.entityName,
        row.entityInfo.entityNamespace,
      ),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      ResourceId: resourceId,
      ResourceName: row._meta?.resourceName,
      Region: row._meta?.region,
    };
  });

  return rows;
};

// Helper function to sort issue rows based on sort model
export const sortIssueRows = (
  rows: (PdpIssueRow | PdpResourceIssueRow)[],
  sortModel: GridSortModel,
): (PdpIssueRow | PdpResourceIssueRow)[] => {
  return rows.sort((a, b) => {
    if (sortModel.length === 0) return 0;

    // Apply all sort criteria in order of priority
    for (const sortItem of sortModel) {
      // Map field names from table headers to data properties
      let fieldName = sortItem.field;
      if (sortItem.field === 'Due Date') fieldName = 'dueDate';
      if (sortItem.field === 'Severity & risk') fieldName = 'severity';
      if (sortItem.field === 'Finding') fieldName = 'issueType';
      if (sortItem.field === 'Entity') fieldName = 'entityInfo';
      if (sortItem.field === 'Owner') fieldName = 'owningTeam';

      const aValue = (a as any)[fieldName];
      const bValue = (b as any)[fieldName];

      // Handle null/undefined values
      if (aValue === null && bValue === null) continue;
      if (aValue === null) return sortItem.sort === 'asc' ? 1 : -1;
      if (bValue === null) return sortItem.sort === 'asc' ? -1 : 1;

      // Special handling for dueDate field using existing comparator
      if (fieldName === 'dueDate') {
        const dueDateComparison = entityDueDateSortComparator(
          aValue.date,
          bValue.date,
        );
        if (dueDateComparison !== 0) {
          return sortItem.sort === 'asc'
            ? dueDateComparison
            : -dueDateComparison;
        }
        continue;
      }

      // Special handling for severity field using existing comparator
      if (fieldName === 'severity') {
        const severityComparison = severitySortComparator(aValue, bValue);
        if (severityComparison !== 0) {
          return sortItem.sort === 'asc'
            ? severityComparison
            : -severityComparison;
        }
        continue;
      }

      // Special handling for Entity field (uses entityRef for sorting)
      if (sortItem.field === 'Entity') {
        const aEntityValue = `${a.entityInfo.entityRef || ''} ${
          a.entityInfo.entityName || ''
        } ${a.entityInfo?.entityNamespace || ''}`.trim();
        const bEntityValue = `${b.entityInfo.entityRef || ''} ${
          b.entityInfo.entityName || ''
        } ${b.entityInfo?.entityNamespace || ''}`.trim();
        const entityComparison = aEntityValue
          .toLowerCase()
          .localeCompare(bEntityValue.toLowerCase());
        if (entityComparison !== 0) {
          return sortItem.sort === 'asc' ? entityComparison : -entityComparison;
        }
        continue;
      }

      // Standard comparison for other fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const stringComparison = aValue
          .toLowerCase()
          .localeCompare(bValue.toLowerCase());
        if (stringComparison !== 0) {
          return sortItem.sort === 'asc' ? stringComparison : -stringComparison;
        }
      } else {
        if (aValue < bValue) {
          return sortItem.sort === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortItem.sort === 'asc' ? 1 : -1;
        }
      }
    }

    return 0;
  });
};

// Helper function to filter issue rows based on filter model
export const filterIssueRows = (
  rows: (PdpIssueRow | PdpResourceIssueRow)[],
  filterModel: GridFilterModel,
): (PdpIssueRow | PdpResourceIssueRow)[] => {
  return rows.filter(row => {
    const quickFilter = filterModel.quickFilterValues?.[0];
    const resourceId =
      row._meta?.resourceId || row._meta?.resourceArn || row.accountId;

    // Handle quick filter search across all row data
    if (quickFilter) {
      const searchableContent = [
        row.issueType,
        row.severity,
        row.entityInfo?.entityName,
        row.entityInfo?.entityNamespace,
        row.owningTeam,
        row.issueDescription,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (!searchableContent.includes(quickFilter.toLowerCase())) {
        return false;
      }
    }

    // Handle column-specific filters
    return filterModel.items.every(filter => {
      if (!filter.value || filter.operator !== 'contains') {
        return true;
      }

      // Map field names from table headers to data properties for filtering
      let fieldName = filter.field;
      if (filter.field === 'Due Date') fieldName = 'dueDate';
      if (filter.field === 'Severity & risk') fieldName = 'severity';
      if (filter.field === 'Finding') fieldName = 'issueType';
      if (filter.field === 'Entity') fieldName = 'entityInfo';
      if (filter.field === 'Owner') fieldName = 'owningTeam';

      const rowValue = (row as any)[fieldName];

      // Handle special cases for complex field types
      if (fieldName === 'dueDate') {
        return (
          String(rowValue.text)
            .toLowerCase()
            .includes(String(filter.value).toLowerCase()) ||
          String(rowValue.date)
            .toLowerCase()
            .includes(String(filter.value).toLowerCase())
        );
      }
      if (fieldName === 'issueType') {
        return (
          String(rowValue)
            .toLowerCase()
            .includes(String(filter.value).toLowerCase()) ||
          String(row.measurement)
            .toLowerCase()
            .includes(String(filter.value).toLowerCase()) ||
          String(row._meta?.resourceName)
            .toLowerCase()
            .includes(String(filter.value).toLowerCase()) ||
          String(resourceId)
            .toLowerCase()
            .includes(String(filter.value).toLowerCase())
        );
      }
      if (fieldName === 'entityInfo') {
        return (
          String(rowValue.entityName)
            .toLowerCase()
            .includes(String(filter.value).toLowerCase()) ||
          String(rowValue.entityNamespace)
            .toLowerCase()
            .includes(String(filter.value).toLowerCase()) ||
          String(row.accountId)
            .toLowerCase()
            .includes(String(filter.value).toLowerCase())
        );
      }

      return String(rowValue)
        .toLowerCase()
        .includes(String(filter.value).toLowerCase());
    });
  });
};

export const useTableRows = (data: IssueData, checks: CheckResources[]) => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  // Initialize sort model to match table headings default sort (Due Date ascending)
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'Due Date',
      sort: 'asc',
    },
  ]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const sortFilteredRows = useMemo(() => {
    return filterIssueRows(
      sortIssueRows(determineIssueRows(data, checks), sortModel),
      filterModel,
    );
  }, [data, checks, sortModel, filterModel]);

  const totalRowCount = useMemo(() => {
    if (!filterModel) {
      return data._meta.total;
    }
    return sortFilteredRows.length;
  }, [data, sortFilteredRows, filterModel]);
  const rows = useMemo(
    () =>
      sortFilteredRows.slice(
        paginationModel.page * paginationModel.pageSize,
        paginationModel.page * paginationModel.pageSize +
          paginationModel.pageSize,
      ),
    [sortFilteredRows, paginationModel],
  );
  return {
    rows,
    totalRowCount,
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
  };
};
